import { z } from 'zod';
import { addressSchema, cents, customFieldValues, dateOnly, email, id, isoDate, listInput, nonEmpty, optionalText, phone, updateOf, url } from './common';

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

export const LIFECYCLE_STAGES = ['prospect', 'lead', 'qualified', 'customer', 'churned'] as const;
export const lifecycleStage = z.enum(LIFECYCLE_STAGES);
export type LifecycleStage = z.infer<typeof lifecycleStage>;

export const LEAD_SOURCES = ['prospecting', 'import', 'website', 'referral', 'inbound_call', 'event', 'partner', 'api', 'other'] as const;
export const leadSource = z.enum(LEAD_SOURCES);
export type LeadSource = z.infer<typeof leadSource>;

/**
 * Legal basis for contacting someone by phone. Drives the national do-not-call check and whether
 * an AI voice may be used (see `@salesbee/domain/compliance`):
 *  - `express_consent`: the person asked to be contacted (form opt-in, recorded consent);
 *  - `existing_business_relationship`: a purchase or inquiry inside the statutory window;
 *  - `b2b_business_line`: a business calling a business on its published business line;
 *  - `none`: unknown. Treated as a consumer call: national registry check required.
 */
export const CONSENT_BASES = ['express_consent', 'existing_business_relationship', 'b2b_business_line', 'none'] as const;
export const consentBasis = z.enum(CONSENT_BASES);
export type ConsentBasis = z.infer<typeof consentBasis>;

export const CONSENT_BASIS_LABELS: Record<ConsentBasis, string> = {
  express_consent: 'Express consent',
  existing_business_relationship: 'Existing business relationship',
  b2b_business_line: 'B2B (business line)',
  none: 'Unknown / none',
};

export const STAGE_KINDS = ['open', 'won', 'lost'] as const;
export const stageKind = z.enum(STAGE_KINDS);
export type StageKind = z.infer<typeof stageKind>;

export const DEAL_STATUSES = ['open', 'won', 'lost'] as const;
export const dealStatus = z.enum(DEAL_STATUSES);
export type DealStatus = z.infer<typeof dealStatus>;

export const ACTIVITY_TYPES = ['note', 'call', 'email', 'sms', 'meeting', 'task_completed', 'stage_change', 'system'] as const;
export const activityType = z.enum(ACTIVITY_TYPES);
export type ActivityType = z.infer<typeof activityType>;

export const TASK_STATUSES = ['todo', 'done', 'cancelled'] as const;
export const taskStatus = z.enum(TASK_STATUSES);
export type TaskStatus = z.infer<typeof taskStatus>;

export const PRIORITIES = ['low', 'normal', 'high'] as const;
export const priority = z.enum(PRIORITIES);
export type Priority = z.infer<typeof priority>;

export const LIST_KINDS = ['static', 'segment'] as const;
export const listKind = z.enum(LIST_KINDS);
export type ListKind = z.infer<typeof listKind>;

/** Strip `.default()`s so an update only touches the fields it names. */
function patchOf<T extends z.ZodRawShape>(create: z.ZodObject<T>): ReturnType<typeof updateOf<T>> {
  const shape: Record<string, z.ZodType> = {};
  for (const [key, field] of Object.entries(create.shape as unknown as Record<string, z.ZodType>)) {
    let inner: z.ZodType = field;
    while (inner instanceof z.ZodDefault) inner = inner.removeDefault() as z.ZodType;
    shape[key] = inner.optional();
  }
  return updateOf(z.object(shape)) as unknown as ReturnType<typeof updateOf<T>>;
}

const tags = z.array(z.string().trim().min(1).max(60)).max(50);

// ---------------------------------------------------------------------------
// Company
// ---------------------------------------------------------------------------

export const createCompanyInput = z.object({
  name: nonEmpty(200),
  domain: z.string().trim().toLowerCase().max(253).optional().nullable(),
  website: url.optional().nullable(),
  phone: phone.optional().nullable(),
  email: email.optional().nullable(),
  industry: optionalText(80),
  employeeCount: z.number().int().min(0).max(10_000_000).optional().nullable(),
  address: addressSchema.optional().nullable(),
  tags: tags.default([]),
  notes: optionalText(),
  ownerId: id.optional().nullable(),
  custom: customFieldValues.default({}),
});
export type CreateCompanyInput = z.infer<typeof createCompanyInput>;
export const updateCompanyInput = patchOf(createCompanyInput);
export type UpdateCompanyInput = z.infer<typeof updateCompanyInput>;

export const listCompaniesInput = listInput.extend({
  industry: z.string().max(80).optional(),
  tag: z.string().max(60).optional(),
  ownerId: id.optional(),
});
export type ListCompaniesInput = z.infer<typeof listCompaniesInput>;

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

export const createContactInput = z.object({
  firstName: z.string().trim().max(120).default(''),
  lastName: z.string().trim().max(120).default(''),
  displayName: z.string().trim().max(240).optional(),
  companyId: id.optional().nullable(),
  jobTitle: optionalText(120),
  email: email.optional().nullable(),
  phone: phone.optional().nullable(),
  mobilePhone: phone.optional().nullable(),
  address: addressSchema.optional().nullable(),
  /** IANA zone of the contact (e.g. America/Vancouver). Inferred from the address when empty. */
  timezone: z.string().trim().max(64).optional().nullable(),
  lifecycleStage: lifecycleStage.default('prospect'),
  source: leadSource.optional().nullable(),
  consentBasis: consentBasis.default('none'),
  consentNote: optionalText(1000),
  consentAt: isoDate.optional().nullable(),
  tags: tags.default([]),
  notes: optionalText(),
  ownerId: id.optional().nullable(),
  custom: customFieldValues.default({}),
});
export type CreateContactInput = z.infer<typeof createContactInput>;
export const updateContactInput = patchOf(createContactInput);
export type UpdateContactInput = z.infer<typeof updateContactInput>;

export const listContactsInput = listInput.extend({
  companyId: id.optional(),
  ownerId: id.optional(),
  tag: z.string().max(60).optional(),
  lifecycleStage: lifecycleStage.optional(),
  consentBasis: consentBasis.optional(),
  listId: id.optional(),
});
export type ListContactsInput = z.infer<typeof listContactsInput>;

// ---------------------------------------------------------------------------
// Pipelines, stages, deals
// ---------------------------------------------------------------------------

export const createStageInput = z.object({
  name: nonEmpty(80),
  kind: stageKind.default('open'),
  probabilityBps: z.number().int().min(0).max(10_000).default(0),
  color: z.string().max(16).optional().nullable(),
});
export type CreateStageInput = z.infer<typeof createStageInput>;

export const createPipelineInput = z.object({
  name: nonEmpty(120),
  isDefault: z.boolean().default(false),
  stages: z.array(createStageInput).min(2).max(20),
});
export type CreatePipelineInput = z.infer<typeof createPipelineInput>;

export const updatePipelineInput = z.object({
  id,
  name: nonEmpty(120).optional(),
  isDefault: z.boolean().optional(),
  /** Full ordered stage list: rows with `id` are updated, rows without are created, missing ones archived. */
  stages: z
    .array(createStageInput.extend({ id: id.optional() }))
    .min(2)
    .max(20)
    .optional(),
});
export type UpdatePipelineInput = z.infer<typeof updatePipelineInput>;

export const createDealInput = z.object({
  title: nonEmpty(200),
  pipelineId: id.optional(),
  stageId: id.optional(),
  contactId: id.optional().nullable(),
  companyId: id.optional().nullable(),
  ownerId: id.optional().nullable(),
  valueCents: cents.min(0).default(0),
  source: leadSource.optional().nullable(),
  expectedCloseDate: dateOnly.optional().nullable(),
  description: optionalText(),
  tags: tags.default([]),
});
export type CreateDealInput = z.infer<typeof createDealInput>;
export const updateDealInput = patchOf(createDealInput.omit({ pipelineId: true, stageId: true }));
export type UpdateDealInput = z.infer<typeof updateDealInput>;

export const moveDealInput = z.object({ id, stageId: id, lostReason: optionalText(500) });
export type MoveDealInput = z.infer<typeof moveDealInput>;

export const listDealsInput = listInput.extend({
  pipelineId: id.optional(),
  stageId: id.optional(),
  status: dealStatus.optional(),
  ownerId: id.optional(),
  contactId: id.optional(),
  companyId: id.optional(),
});
export type ListDealsInput = z.infer<typeof listDealsInput>;

// ---------------------------------------------------------------------------
// Activities, tasks
// ---------------------------------------------------------------------------

export const createActivityInput = z.object({
  type: activityType.default('note'),
  subject: optionalText(255),
  body: optionalText(20_000),
  occurredAt: isoDate.optional(),
  durationMinutes: z.number().int().min(0).max(24 * 60).optional().nullable(),
  contactId: id.optional().nullable(),
  companyId: id.optional().nullable(),
  dealId: id.optional().nullable(),
});
export type CreateActivityInput = z.infer<typeof createActivityInput>;

export const listActivitiesInput = listInput.extend({
  contactId: id.optional(),
  companyId: id.optional(),
  dealId: id.optional(),
  type: activityType.optional(),
});
export type ListActivitiesInput = z.infer<typeof listActivitiesInput>;

export const createTaskInput = z.object({
  title: nonEmpty(255),
  description: optionalText(),
  priority: priority.default('normal'),
  dueAt: isoDate.optional().nullable(),
  assigneeId: id.optional().nullable(),
  contactId: id.optional().nullable(),
  companyId: id.optional().nullable(),
  dealId: id.optional().nullable(),
});
export type CreateTaskInput = z.infer<typeof createTaskInput>;
export const updateTaskInput = patchOf(createTaskInput).extend({ status: taskStatus.optional() });
export type UpdateTaskInput = z.infer<typeof updateTaskInput>;

export const listTasksInput = listInput.extend({
  status: taskStatus.optional(),
  assigneeId: id.optional(),
  mine: z.boolean().optional(),
  dealId: id.optional(),
  contactId: id.optional(),
  dueBefore: isoDate.optional(),
});
export type ListTasksInput = z.infer<typeof listTasksInput>;

// ---------------------------------------------------------------------------
// Lists and segments
// ---------------------------------------------------------------------------

/** Dynamic segment: contacts matching every given filter. */
export const segmentFiltersSchema = z.object({
  tags: z.array(z.string().max(60)).max(20).optional(),
  lifecycleStages: z.array(lifecycleStage).max(LIFECYCLE_STAGES.length).optional(),
  consentBases: z.array(consentBasis).max(CONSENT_BASES.length).optional(),
  sources: z.array(leadSource).max(LEAD_SOURCES.length).optional(),
  regions: z.array(z.string().trim().toUpperCase().max(2)).max(80).optional(),
  ownerId: id.optional(),
  hasPhone: z.boolean().optional(),
  companyId: id.optional(),
  search: z.string().max(200).optional(),
});
export type SegmentFilters = z.infer<typeof segmentFiltersSchema>;

export const createListInput = z.object({
  name: nonEmpty(120),
  description: optionalText(1000),
  kind: listKind.default('static'),
  filters: segmentFiltersSchema.default({}),
});
export type CreateListInput = z.infer<typeof createListInput>;
export const updateListInput = z.object({
  id,
  name: nonEmpty(120).optional(),
  description: optionalText(1000),
  filters: segmentFiltersSchema.optional(),
});

export const listMembersInput = z.object({ listId: id, contactIds: z.array(id).min(1).max(500) });

// ---------------------------------------------------------------------------
// CSV import / export, search
// ---------------------------------------------------------------------------

export const CONTACT_IMPORT_FIELDS = [
  'firstName',
  'lastName',
  'displayName',
  'email',
  'phone',
  'mobilePhone',
  'jobTitle',
  'companyName',
  'city',
  'region',
  'country',
  'postalCode',
  'timezone',
  'consentBasis',
  'tags',
  'notes',
] as const;
export const contactImportField = z.enum(CONTACT_IMPORT_FIELDS);
export type ContactImportField = z.infer<typeof contactImportField>;

export const csvImportInput = z.object({
  csv: z.string().min(1).max(2_000_000),
  /** CSV header -> field. Unmapped headers are ignored. */
  mapping: z.record(z.string(), contactImportField),
  /** Duplicates (same email or phone): skip them or update the existing contact. */
  dedupe: z.enum(['skip', 'update']).default('skip'),
  /** Add every imported contact to this static list. */
  listId: id.optional(),
  defaultConsentBasis: consentBasis.default('none'),
  tag: z.string().trim().max(60).optional(),
});
export type CsvImportInput = z.infer<typeof csvImportInput>;

export const csvImportResult = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
  errors: z.array(z.object({ row: z.number().int(), message: z.string() })),
});
export type CsvImportResult = z.infer<typeof csvImportResult>;

export const exportInput = z.object({ entity: z.enum(['contacts', 'companies', 'deals']), listId: id.optional() });

export const searchInput = z.object({ q: z.string().trim().min(1).max(200), limit: z.number().int().min(1).max(25).default(8) });
